import { useGetAgendasQuery } from 'store/services/flow/agenda';
import { useGetAllViewsQuery } from 'store/services/flow/views';
import { useGetDatasourcesQuery } from 'store/services/helios/dataSources';
import { useGetModulesQuery } from 'store/services/core/modules';
import { type Dispatch, type SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { MenuItem, styled } from '@mui/material';

import { Autocomplete, type Option } from 'components/mui/common/Autocomplete';
import { ActionModal } from 'components/mui/common/ActionModal';
import { TextField } from 'components/mui/common/TextField';
import { Select } from 'components/mui/common/Select';
import type { KpiItem as KpiItemType, KpiView } from 'types/dashboard';
import type { ModalsType } from 'types';
import { ColorInput } from 'components/common/Inputs';
import { DATASOURCES_QUERY_PARAMS } from 'components/admin/helios/Functions/HeliosFunctionsTable';
import { useGetTemplateByIdQuery, useGetKpiTemplatesByModuleIdQuery } from 'store/services/helios/templates';
import { useDashboardAccess } from 'contexts/DashboardAccessContext';

const Content = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

interface Props extends ModalsType {
  onConfirm: () => void;
  setKpiData: Dispatch<SetStateAction<KpiItemType>>;
  kpiData?: KpiItemType;
  isHeliosModule: boolean;
}

type KpiFormValues = {
  id: string;
  borderColor?: string;
  name?: string;
  link?: { link: string; linkText: string };
  isNew?: boolean;
  view?: {
    moduleId: string;
    agendaId: string | Option;
    viewId: string;
  };
  heliosSqlScript?: {
    heliosDataSource: string;
    heliosScript: string;
    moduleId: string;
    agendaId: string | Option;
    viewId: string;
  };
};

type TabItem = 'link' | 'view' | 'heliosSqlScript';

export const KpiModal = ({ open, onClose, onConfirm, setKpiData, kpiData, isHeliosModule }: Props) => {
  const { t } = useTranslation();
  const { isUserDashboard, accessibleModuleIds, accessibleAgendaIds } = useDashboardAccess();
  const [currentTab, setCurrentTab] = useState<TabItem>('link');

  useEffect(() => {
    const kpi = kpiData?.kpi;
    if (!kpi) return;

    if (kpi.heliosSqlScript) {
      setCurrentTab('heliosSqlScript');
      return;
    }

    const oldKpi = kpi as unknown as KpiView;

    if (kpi.view || (oldKpi.viewId && oldKpi.agendaId)) {
      setCurrentTab('view');
      return;
    }

    setCurrentTab('link');
  }, [kpiData]);

  const defaultValues = useMemo(() => {
    const kpi = kpiData?.kpi;
    const viewData = (kpi?.view ? kpi.view : kpi) as KpiView;

    return {
      id: kpi?.id ?? '',
      borderColor: kpiData?.borderColor ?? '',
      name: kpiData?.name ?? '',
      link: {
        link: kpi?.link?.link ?? '',
        linkText: kpi?.link?.linkText ?? '',
      },
      view: {
        moduleId: viewData?.moduleId ?? '',
        agendaId: viewData?.agendaId ?? '',
        viewId: viewData?.viewId ?? '',
      },
      heliosSqlScript: {
        heliosDataSource: kpi?.heliosSqlScript?.heliosDataSource ?? '',
        heliosScript: kpi?.heliosSqlScript?.heliosScript ?? '',
        moduleId: kpi?.heliosSqlScript?.moduleId ?? '',
        agendaId: kpi?.heliosSqlScript?.agendaId ?? '',
        viewId: kpi?.heliosSqlScript?.viewId ?? '',
      },
    };
  }, [kpiData?.borderColor, kpiData?.kpi, kpiData?.name]);

  const { control, handleSubmit, setValue, watch, reset } = useForm<KpiFormValues>({
    defaultValues,
  });

  const [watchViewModuleId, watchViewAgendaId, watchViewViewId, watchHeliosModuleId, watchHeliosAgendaId, watchHeliosViewId] = watch([
    'view.moduleId',
    'view.agendaId',
    'view.viewId',
    'heliosSqlScript.moduleId',
    'heliosSqlScript.agendaId',
    'heliosSqlScript.viewId',
  ]);

  const { data: heliosDatasources } = useGetDatasourcesQuery(DATASOURCES_QUERY_PARAMS);

  const { data: modulesData, isSuccess: modulesLoaded } = useGetModulesQuery({
    page: 1,
    pageSize: 1000,
    search: '',
    orderBy: 'id',
    orderDesc: false,
  });

  const selectedModuleId = watchViewModuleId || watchHeliosModuleId;

  const selectedModule = useMemo(() => {
    return modulesData?.items?.find((m) => m.id === selectedModuleId);
  }, [modulesData?.items, selectedModuleId]);

  const hasHeliosModule = Boolean(selectedModule?.isHeliosVisible);

  const { data: agendasData, isSuccess: agendasLoaded } = useGetAgendasQuery({
    page: 1,
    pageSize: 1000,
    search: '',
    orderBy: 'id',
    orderDesc: false,
    ModuleIds: selectedModuleId ? [selectedModuleId] : undefined,
    Name: '',
    AgendaId: '',
    CreateBy: '',
    Created: '',
    Status: '',
  });

  const { data: templates = [] } = useGetKpiTemplatesByModuleIdQuery(selectedModuleId, {
    skip: !hasHeliosModule,
    refetchOnMountOrArgChange: true,
  });

  const moduleOptions = useMemo(
    () => (modulesData?.items ?? []).filter((module) => !isUserDashboard || accessibleModuleIds.has(module.id)),
    [accessibleModuleIds, isUserDashboard, modulesData?.items]
  );

  const agendasOptions = useMemo(() => {
    const agendas = (agendasData?.items ?? []).filter((agenda) => !isUserDashboard || accessibleAgendaIds.has(agenda.id.toLowerCase()));
    const templateItems = (templates ?? []).filter((template) => !isUserDashboard || accessibleAgendaIds.has(template.id.toLowerCase()));

    return [
      agendas.map((agenda) => ({ label: agenda.name, value: agenda.id, isTemplate: false })),
      templateItems.map((template) => ({ label: template.name, value: template.id, isTemplate: true })),
    ].flatMap((items) => items);
  }, [accessibleAgendaIds, agendasData?.items, isUserDashboard, templates]);

  const getOptionValue = (option: string | Option | undefined): string => {
    if (!option) return '';
    if (typeof option === 'object' && 'value' in option) {
      return option.value;
    }
    return option as string;
  };

  const agendaId = getOptionValue(watchViewAgendaId) || getOptionValue(watchHeliosAgendaId);
  const selectedAgendaOption = agendasOptions.find((o) => o.value === agendaId);
  const isTemplateSelected = Boolean(selectedAgendaOption?.isTemplate);
  const isViewIdSelectDisabled = !agendasLoaded || watchViewAgendaId === null || watchViewAgendaId === '' || watchViewModuleId === '';

  const isHeliosViewIdSelectDisabled = !agendasLoaded || watchHeliosAgendaId === null || watchHeliosAgendaId === '';

  const { data: viewsData } = useGetAllViewsQuery(
    {
      parentId: agendaId,
      viewParentType: 'Agenda',
      ExcludeHiddenViews: true,
    },
    {
      skip: !agendaId,
      refetchOnMountOrArgChange: true,
    }
  );

  const selectedView = viewsData?.views?.find((view) => view.viewId === watchViewViewId || watchHeliosViewId);

  const { data: templateData } = useGetTemplateByIdQuery({ id: agendaId ?? '' }, { skip: !isTemplateSelected || !agendaId });

  const onSubmit = useCallback(
    async (data: KpiFormValues) => {
      const name = data.name ? data.name : selectedView?.viewName || '';
      let payload = {
        id: kpiData?.id ?? null,
        order: kpiData?.order || 0,
        borderColor: data.borderColor ?? '',
        type: 'KPI' as const,
        name,
        isNew: false,
        kpi: {},
      };

      switch (currentTab) {
        case 'link':
          payload = {
            ...payload,
            kpi: {
              id: !data.id || data.id === '' ? null : data.id,
              link: {
                link: data.link?.link,
                linkText: data.link?.linkText,
              },
            },
          };
          break;

        case 'view':
          payload = {
            ...payload,
            kpi: {
              id: !data.id || data.id === '' ? null : data.id,
              view: {
                moduleId: data.view?.moduleId,
                agendaId: typeof data.view?.agendaId === 'object' ? data.view?.agendaId?.value : data.view?.agendaId,
                viewId: data.view?.viewId,
                ...(templateData && {
                  heliosDataSource: templateData.datasourceId,
                }),
              },
            },
          };
          break;

        case 'heliosSqlScript':
          payload = {
            ...payload,

            kpi: {
              id: !data.id || data.id === '' ? null : data.id,
              heliosSqlScript: {
                heliosDataSource: data.heliosSqlScript?.heliosDataSource,
                heliosScript: data.heliosSqlScript?.heliosScript,
                moduleId: data.heliosSqlScript?.moduleId,
                agendaId: typeof data.heliosSqlScript?.agendaId === 'object' ? data.heliosSqlScript?.agendaId?.value : data.heliosSqlScript?.agendaId,
                viewId: data.heliosSqlScript?.viewId,
              },
            },
          };
          break;

        default:
          break;
      }

      setKpiData(payload as KpiItemType);

      onConfirm();
    },
    [selectedView?.viewName, kpiData?.id, kpiData?.order, currentTab, setKpiData, onConfirm, templateData]
  );

  return (
    <ActionModal
      open={open}
      onClose={onClose}
      onCancel={onClose}
      onSubmit={handleSubmit(onSubmit)}
      title={t('core.settings.dashboard.kpiSettings')}
      submitText={t('common.confirm')}>
      <Content>
        <Select
          value={currentTab}
          onChange={(e) => {
            reset();
            setCurrentTab(e.target.value as TabItem);
          }}
          fullWidth
          hideDefaultItem
          label={t('core.settings.dashboard.sourceSelect')}>
          <MenuItem value="link">{t('core.settings.dashboard.link')}</MenuItem>
          <MenuItem value="view">{t('core.settings.dashboard.view')}</MenuItem>
          {isHeliosModule && <MenuItem value="heliosSqlScript">{t('core.settings.dashboard.heliosSqlScript')}</MenuItem>}
        </Select>

        {currentTab !== 'link' && (
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField label={t('common.title')} placeholder={selectedView?.viewName || t('common.noRequiredField')} {...field} fullWidth />
            )}
          />
        )}

        {currentTab === 'link' && (
          <>
            <Controller
              name="link.link"
              rules={{ required: t('common.required') }}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField {...field} fullWidth label={t('core.settings.dashboard.linkUrl')} error={Boolean(error)} helperText={error?.message} />
              )}
            />
            <Controller
              name="link.linkText"
              rules={{ required: t('common.required') }}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField {...field} fullWidth label={t('core.settings.dashboard.linkText')} error={Boolean(error)} helperText={error?.message} />
              )}
            />
          </>
        )}

        {currentTab === 'view' && (
          <>
            <Controller
              name="view.moduleId"
              rules={{ required: t('common.required') }}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Select
                  {...field}
                  fullWidth
                  error={Boolean(error)}
                  label={t('core.settings.dashboard.modules')}
                  displayEmpty
                  helperText={error?.message}
                  onChange={(e) => {
                    field.onChange(e);
                    setValue('view.agendaId', '');
                    setValue('view.viewId', '');
                  }}>
                  {moduleOptions.map((module) => (
                    <MenuItem key={module.id} value={module.id}>
                      {module.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
            <Controller
              control={control}
              name="view.agendaId"
              rules={{
                required: {
                  value: true,
                  message: t('common.required'),
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <Autocomplete
                  data-testid="records-agenda-field"
                  placeholder={t('common.select')}
                  label={t('sidebar.flow.agendas')}
                  disabled={!modulesLoaded || watchViewModuleId === ''}
                  options={agendasOptions}
                  {...field}
                  value={field.value || ''}
                  onChange={(e) => {
                    field.onChange(e);
                    setValue('view.viewId', '');
                  }}
                  error={Boolean(error)}
                  helperText={error?.message}
                  background="white"
                />
              )}
            />
            {!isTemplateSelected && (
              <Controller
                name="view.viewId"
                rules={{ required: t('common.required') }}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Select
                    {...field}
                    fullWidth
                    error={Boolean(error)}
                    helperText={error?.message}
                    label={t('core.settings.dashboard.views')}
                    displayEmpty
                    disabled={isViewIdSelectDisabled}>
                    {(viewsData?.views ?? []).map((view) => (
                      <MenuItem key={view.viewId} value={view.viewId}>
                        {view.viewName}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            )}
          </>
        )}

        {currentTab === 'heliosSqlScript' && (
          <>
            <Controller
              name="heliosSqlScript.heliosDataSource"
              rules={{ required: t('common.required') }}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Select
                  {...field}
                  fullWidth
                  label={t('core.settings.dashboard.heliosDataSource')}
                  error={Boolean(error)}
                  helperText={error?.message}
                  background="white"
                  displayEmpty>
                  {(heliosDatasources?.items ?? []).map((source) => (
                    <MenuItem key={source.id} value={source.id}>
                      {source.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
            <Controller
              name="heliosSqlScript.heliosScript"
              rules={{ required: t('common.required') }}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  fullWidth
                  multiline
                  minRows={4}
                  label={t('core.settings.dashboard.heliosScript')}
                  error={Boolean(error)}
                  helperText={error?.message}
                />
              )}
            />
            <Controller
              name="heliosSqlScript.moduleId"
              rules={{ required: t('common.required') }}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Select
                  {...field}
                  fullWidth
                  error={Boolean(error)}
                  label={t('core.settings.dashboard.modules')}
                  displayEmpty
                  helperText={error?.message}
                  onChange={(e) => {
                    field.onChange(e);
                    setValue('heliosSqlScript.agendaId', '');
                    setValue('heliosSqlScript.viewId', '');
                  }}>
                  {moduleOptions.map((module) => (
                    <MenuItem key={module.id} value={module.id}>
                      {module.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
            <Controller
              control={control}
              name="heliosSqlScript.agendaId"
              rules={{
                required: {
                  value: true,
                  message: t('common.required'),
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <Autocomplete
                  data-testid="records-agenda-field"
                  placeholder={t('common.select')}
                  label={t('sidebar.flow.agendas')}
                  disabled={!modulesLoaded || watchHeliosModuleId === ''}
                  options={agendasOptions}
                  {...field}
                  value={field.value || ''}
                  onChange={(e) => {
                    field.onChange(e);
                    setValue('heliosSqlScript.viewId', '');
                  }}
                  error={Boolean(error)}
                  helperText={error?.message}
                  background="white"
                />
              )}
            />
            {!isTemplateSelected && (
              <Controller
                name="heliosSqlScript.viewId"
                rules={{ required: t('common.required') }}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Select
                    {...field}
                    fullWidth
                    error={Boolean(error)}
                    helperText={error?.message}
                    label={t('core.settings.dashboard.views')}
                    displayEmpty
                    disabled={isHeliosViewIdSelectDisabled}>
                    {(viewsData?.views ?? []).map((view) => (
                      <MenuItem key={view.viewId} value={view.viewId}>
                        {view.viewName}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            )}
          </>
        )}
        <Controller
          control={control}
          name="borderColor"
          render={({ field }) => <ColorInput {...field} value={field?.value} label={t('core.settings.dashboard.color')} />}
        />
      </Content>
    </ActionModal>
  );
};
